/**
 * Marketing landing — public launch.
 *
 * Sections (top → bottom):
 *   Header → Hero → Trust band → ChromaticGrid → ProductPreview → HowItWorks →
 *   AgentBand → Faq → PricingTeaser → FinalCta → Footer
 *
 * SEO meta + OG tags + JSON-LD live in apps/landing_page/index.html.
 */

import { useState, type CSSProperties, type FormEvent } from "react";

import { domAnimation, LazyMotion, MotionConfig } from "framer-motion";
import { Menu, X } from "lucide-react";

import { LogoMark, LogoWordmark } from "@m2s/design-system";

import { HeroBackground } from "./hero/HeroBackground";
import { useIsMobile } from "./lib/useIsMobile";
import { SmoothScroll } from "./motion/SmoothScroll";
import { Parallax, Reveal, Tilt } from "./motion/reveal";

// Where Sign-in / Sign-up links land. Override per env via VITE_APP_URL.
// In dev that's typically http://lighthouse.localhost:5273; in prod it's app.minute2sec.com.
const APP_URL =
  (import.meta.env.VITE_APP_URL as string | undefined) ??
  (import.meta.env.PROD
    ? "https://app.minute2sec.com/login"
    : "http://lighthouse.localhost:5273/login");
const APP_SIGNUP_URL = APP_URL;

// Backend API base. Prod: same-origin "/api/v1" (Caddy proxies /api/* to the
// backend). Dev: hit the docker-exposed backend directly. Override via
// VITE_MARKETING_API_URL.
const API_BASE =
  (import.meta.env.VITE_MARKETING_API_URL as string | undefined) ??
  (import.meta.env.PROD ? "/api/v1" : "http://localhost:8100/api/v1");

export function App() {
  return (
    <LazyMotion features={domAnimation} strict>
      <MotionConfig reducedMotion="user">
        <SmoothScroll>
          <div style={{ background: "var(--canvas)", overflow: "hidden" }}>
            <Header />
            <Hero />
            <TrustBand />
            <ChromaticGrid />
            <ProductPreview />
            <HowItWorks />
            <AgentBand />
            <Faq />
            <PricingTeaser />
            <FinalCta />
            <Contact />
            <Footer />
          </div>
        </SmoothScroll>
      </MotionConfig>
    </LazyMotion>
  );
}

/* Reveal / Parallax / Tilt motion primitives now live in ./motion/reveal
   (framer-motion, reduced-motion aware). `Reveal` keeps the same API. */

/* ---------- Header ---------- */

const NAV_LINKS = [
  { label: "Product", href: "#features" },
  { label: "How it works", href: "#how-it-works" },
  { label: "Agents", href: "#agents" },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact", href: "#contact" },
];

function Header() {
  const isMobile = useIsMobile(860);
  const [open, setOpen] = useState(false);
  const showMenu = isMobile && open;
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        backdropFilter: "saturate(150%) blur(8px)",
        WebkitBackdropFilter: "saturate(150%) blur(8px)",
        background: "rgba(255,255,255,0.78)",
        borderBottom: "1px solid var(--hairline)",
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "16px 32px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <a
          href="/"
          style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}
          aria-label="minute2sec home"
        >
          <LogoMark size={32} />
          <LogoWordmark size={17} />
        </a>

        {isMobile ? (
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            style={{
              marginLeft: "auto",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              border: "1px solid var(--hairline)",
              borderRadius: 6,
              background: "var(--canvas)",
              color: "var(--ink)",
              cursor: "pointer",
            }}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        ) : (
          <nav style={{ marginLeft: "auto", display: "flex", gap: 28, alignItems: "center" }}>
            {NAV_LINKS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "var(--body)",
                  textDecoration: "none",
                }}
              >
                {item.label}
              </a>
            ))}
            <a
              href={APP_URL}
              style={{ fontSize: 14, fontWeight: 500, color: "var(--ink)", textDecoration: "none" }}
            >
              Sign in
            </a>
            <a
              href={APP_SIGNUP_URL}
              style={{
                fontSize: 14,
                fontWeight: 500,
                background: "var(--ink)",
                color: "#fff",
                padding: "8px 14px",
                borderRadius: 4,
                textDecoration: "none",
              }}
            >
              Start free
            </a>
          </nav>
        )}
      </div>

      {showMenu && (
        <nav
          style={{
            borderTop: "1px solid var(--hairline)",
            background: "var(--canvas)",
            padding: "8px 32px 20px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {NAV_LINKS.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={() => setOpen(false)}
              style={{
                padding: "11px 0",
                fontSize: 15,
                fontWeight: 500,
                color: "var(--body)",
                textDecoration: "none",
                borderBottom: "1px solid var(--hairline)",
              }}
            >
              {item.label}
            </a>
          ))}
          <a
            href={APP_URL}
            onClick={() => setOpen(false)}
            style={{
              padding: "12px 0 4px",
              fontSize: 15,
              fontWeight: 500,
              color: "var(--ink)",
              textDecoration: "none",
            }}
          >
            Sign in
          </a>
          <a
            href={APP_SIGNUP_URL}
            onClick={() => setOpen(false)}
            style={{
              marginTop: 8,
              textAlign: "center",
              background: "var(--ink)",
              color: "#fff",
              padding: "12px 14px",
              borderRadius: 4,
              fontSize: 15,
              fontWeight: 500,
              textDecoration: "none",
            }}
          >
            Start free
          </a>
        </nav>
      )}
    </header>
  );
}

/* ---------- Hero ---------- */

function Hero() {
  return (
    <section style={{ position: "relative", overflow: "hidden" }}>
      {/* Static aurora + lazy WebGL particle scene (reduced-motion / mobile / no-WebGL safe). */}
      <HeroBackground />
      <div
        style={{
          position: "relative",
          maxWidth: 1280,
          margin: "0 auto",
          padding: "112px 32px 128px",
          textAlign: "center",
        }}
      >
        <Reveal>
          <a
            href="#how-it-works"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "5px 12px 5px 6px",
              marginBottom: 28,
              fontSize: 12.5,
              color: "var(--body)",
              textDecoration: "none",
              border: "1px solid var(--hairline)",
              borderRadius: 999,
              background: "var(--canvas)",
            }}
          >
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                padding: "2px 8px",
                borderRadius: 999,
                background: "var(--ink)",
                color: "#fff",
              }}
            >
              New
            </span>
            GitHub auto-linking · See how it works →
          </a>
        </Reveal>

        <Reveal delay={80}>
          <div style={{ display: "inline-flex", justifyContent: "center", marginBottom: 24 }}>
            <LogoMark size={84} withDial animated />
          </div>
        </Reveal>

        <Reveal delay={160}>
          <h1
            style={{
              fontSize: "clamp(48px, 7vw, 88px)",
              fontWeight: 600,
              letterSpacing: "-1.2px",
              lineHeight: 1.04,
              margin: "0 auto 24px",
              maxWidth: 980,
              color: "var(--ink)",
            }}
          >
            Tasks, docs, time.
            <br />
            <span style={{ color: "var(--mute)" }}>One workspace.</span>
          </h1>
        </Reveal>

        <Reveal delay={240}>
          <p
            style={{
              fontSize: "clamp(17px, 1.5vw, 21px)",
              color: "var(--body)",
              lineHeight: 1.55,
              maxWidth: 680,
              margin: "0 auto 40px",
            }}
          >
            Task tracking, docs, and integrated timesheets in a single product. Built API-first with
            MCP, so LLM agents work alongside your team — not as a separate stack.
          </p>
        </Reveal>

        <Reveal delay={320}>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <a
              href={APP_SIGNUP_URL}
              style={{
                background: "var(--ink)",
                color: "#fff",
                padding: "13px 22px",
                borderRadius: 4,
                fontSize: 15,
                fontWeight: 500,
                textDecoration: "none",
                boxShadow: "0 12px 24px rgba(0,0,0,0.18), 0 4px 8px rgba(0,0,0,0.08)",
              }}
            >
              Start free →
            </a>
            <a
              href="#features"
              style={{
                border: "1px solid var(--hairline-strong)",
                color: "var(--ink)",
                padding: "13px 22px",
                borderRadius: 4,
                fontSize: 15,
                fontWeight: 500,
                textDecoration: "none",
                background: "var(--canvas)",
              }}
            >
              See the pitch
            </a>
          </div>
        </Reveal>

        <Reveal delay={420}>
          <p
            style={{
              marginTop: 24,
              fontSize: 12.5,
              color: "var(--mute)",
              fontFamily: "var(--font-mono)",
              letterSpacing: 0.2,
            }}
          >
            no credit card · up to 5 users free forever · MCP-ready out of the box
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Trust band ---------- */

function TrustBand() {
  const stats = [
    { value: "1 workspace", label: "Tasks, docs, time — no tab-juggling" },
    { value: "MCP-native", label: "Agents act as first-class users" },
    { value: "Branch → task", label: "PRs + commits auto-attach to the work" },
    { value: "Per-user analytics", label: "See exactly what you shipped" },
  ];
  return (
    <section
      style={{
        borderTop: "1px solid var(--hairline)",
        borderBottom: "1px solid var(--hairline)",
        background: "var(--surface-1)",
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "32px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 24,
        }}
      >
        {stats.map((s) => (
          <Reveal key={s.value}>
            <div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  letterSpacing: "-0.2px",
                  color: "var(--ink)",
                  marginBottom: 4,
                }}
              >
                {s.value}
              </div>
              <div style={{ fontSize: 13, color: "var(--body-mid)", lineHeight: 1.45 }}>
                {s.label}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------- Chromatic feature grid ---------- */

function ChromaticGrid() {
  const cards = [
    {
      color: "#7a3dff",
      eyebrow: "Tasks",
      title: "Boards your team will actually use.",
      copy: "Linear-density board with epics and tasks, drag-and-drop transitions, sprint-aware status columns, optimistic updates.",
    },
    {
      color: "#ed52cb",
      eyebrow: "Docs",
      title: "One source of truth.",
      copy: "Hierarchical spaces, markdown editor, a page tree with sub-pages, starter templates for RFCs and runbooks.",
    },
    {
      color: "#3b89ff",
      eyebrow: "Time",
      title: "Logged where the work happens.",
      copy: "Jira-style monthly grid, per-cell upserts, leave balances, manager approvals.",
    },
  ];
  return (
    <section
      id="features"
      style={{ maxWidth: 1280, margin: "0 auto", padding: "80px 32px 40px" }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 16,
        }}
      >
        {cards.map((c, i) => (
          <Reveal key={c.eyebrow} delay={i * 100} style={{ height: "100%" }}>
            <Tilt lift={10} style={{ height: "100%" }}>
              <div
                style={{
                  background: c.color,
                  color: "#fff",
                  padding: 32,
                  borderRadius: 12,
                  minHeight: 280,
                  height: "100%",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: `0 26px 60px -26px ${c.color}bb, 0 10px 24px -14px ${c.color}88`,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: 1.4,
                    textTransform: "uppercase",
                    opacity: 0.88,
                  }}
                >
                  {c.eyebrow}
                </span>
                <div>
                  <div
                    style={{
                      fontSize: 28,
                      fontWeight: 600,
                      letterSpacing: "-0.3px",
                      marginBottom: 12,
                    }}
                  >
                    {c.title}
                  </div>
                  <p style={{ fontSize: 16, lineHeight: 1.55, opacity: 0.92, margin: 0 }}>{c.copy}</p>
                </div>
              </div>
            </Tilt>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------- Live product preview ---------- */

function ProductPreview() {
  const isMobile = useIsMobile();
  return (
    <section style={{ maxWidth: 1280, margin: "0 auto", padding: "40px 32px 80px" }}>
      <Reveal>
        <Parallax speed={0.12}>
        <div
          style={{
            border: "1px solid var(--hairline)",
            borderRadius: 12,
            background: "var(--canvas)",
            boxShadow:
              "0 40px 80px -32px rgba(8,8,8,0.22), 0 30px 40px rgba(0,0,0,0.06), 0 13px 18px rgba(0,0,0,0.05), 0 4px 8px rgba(0,0,0,0.04)",
            overflow: "hidden",
          }}
        >
          {/* Mock window chrome */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "10px 14px",
              borderBottom: "1px solid var(--hairline)",
              background: "var(--surface-1)",
            }}
          >
            <span style={dot("#ee1d36")} />
            <span style={dot("#ffae13")} />
            <span style={dot("#00d722")} />
            <span
              style={{
                marginLeft: 12,
                fontSize: 12,
                fontFamily: "var(--font-mono)",
                color: "var(--mute)",
              }}
            >
              your-team.minute2sec.com
            </span>
          </div>
          {/* Mock board */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "180px 1fr",
              minHeight: 320,
            }}
          >
            <aside
              style={{
                borderRight: isMobile ? "none" : "1px solid var(--hairline)",
                borderBottom: isMobile ? "1px solid var(--hairline)" : "none",
                padding: "16px 12px",
                background: "var(--surface-1)",
              }}
            >
              <div style={sideLabel}>Workspace</div>
              <div style={sideRow(true)}>
                <span style={swatch("#7a3dff")} />
                Board
              </div>
              <div style={sideRow(false)}>
                <span style={swatch("#ed52cb")} />
                Docs
              </div>
              <div style={sideRow(false)}>
                <span style={swatch("#3b89ff")} />
                Time
              </div>
              <div style={{ ...sideLabel, marginTop: 16 }}>Projects</div>
              <div style={sideRow(false)}>
                <span style={swatch("#7a3dff")} /> Apollo
              </div>
              <div style={sideRow(false)}>
                <span style={swatch("#ed52cb")} /> Bedrock
              </div>
              <div style={sideRow(false)}>
                <span style={swatch("#3b89ff")} /> Curie
              </div>
            </aside>
            <div style={{ padding: 18 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                  gap: 12,
                  height: "100%",
                }}
              >
                <BoardColumn title="Todo" count={4} cards={["APO-12", "APO-14", "APO-15", "APO-17"]} />
                <BoardColumn
                  title="In progress"
                  count={3}
                  cards={["APO-9", "APO-11", "APO-13"]}
                  highlight="APO-11"
                />
                <BoardColumn title="Done" count={5} cards={["APO-3", "APO-5", "APO-7", "APO-8"]} />
              </div>
            </div>
          </div>
        </div>
        </Parallax>
      </Reveal>
    </section>
  );
}

function BoardColumn({
  title,
  count,
  cards,
  highlight,
}: {
  title: string;
  count: number;
  cards: string[];
  highlight?: string;
}) {
  return (
    <div
      style={{
        background: "var(--surface-1)",
        borderRadius: 6,
        padding: 10,
        border: "1px solid var(--hairline)",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: "var(--mute)",
          marginBottom: 8,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {title}
        <span style={{ fontFamily: "var(--font-mono)" }}>·</span>
        <span style={{ fontFamily: "var(--font-mono)", color: "var(--body-mid)" }}>{count}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {cards.map((key) => {
          const isHighlight = key === highlight;
          return (
            <div
              key={key}
              style={{
                background: "var(--canvas)",
                border: "1px solid var(--hairline)",
                borderLeft: isHighlight
                  ? "3px solid var(--accent-purple)"
                  : "1px solid var(--hairline)",
                borderRadius: 4,
                padding: "8px 10px",
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                gap: 8,
                boxShadow: isHighlight ? "0 6px 14px rgba(122,61,255,0.18)" : undefined,
                position: "relative",
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--body-mid)",
                }}
              >
                {key}
              </span>
              <span style={{ flex: 1, color: "var(--ink)" }}>Task title…</span>
              {isHighlight && (
                <span
                  aria-hidden
                  style={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: "#ff6b00",
                    boxShadow: "0 0 0 4px rgba(255,107,0,0.18)",
                    animation: "m2s-dial-pulse 1.6s ease-in-out infinite",
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- How it works ---------- */

function HowItWorks() {
  const steps = [
    {
      n: "01",
      title: "Spin up your workspace",
      body: "Sign up with email, pick a tenant slug, and invite your team. Every workspace gets its own subdomain and isolated data.",
    },
    {
      n: "02",
      title: "Work where the work happens",
      body: "Board for tasks, docs for context, timesheet for hours. Drag cards across columns. Write pages with sub-pages. Log time in a Jira-style grid.",
    },
    {
      n: "03",
      title: "Connect GitHub, let AI help",
      body: "Paste a read-only PAT and we auto-link your PRs and commits to tasks via branch prefix. MCP exposes every action to LLM agents — Atlas drafts, you approve.",
    },
  ];
  return (
    <section
      id="how-it-works"
      style={{ maxWidth: 1280, margin: "0 auto", padding: "100px 32px" }}
    >
      <Reveal>
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: "var(--mute)",
          }}
        >
          How it works
        </span>
      </Reveal>
      <Reveal delay={80}>
        <h2
          style={{
            fontSize: "clamp(32px, 4vw, 48px)",
            fontWeight: 600,
            letterSpacing: "-0.5px",
            margin: "12px 0 40px",
            maxWidth: 720,
          }}
        >
          From signup to shipping in three steps.
        </h2>
      </Reveal>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 24,
        }}
      >
        {steps.map((s, i) => (
          <Reveal key={s.n} delay={i * 100}>
            <div
              style={{
                position: "relative",
                padding: "28px 24px 24px",
                background: "var(--canvas)",
                border: "1px solid var(--hairline)",
                borderRadius: 12,
                minHeight: 220,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 1.4,
                  color: "var(--brand-teal, #0F766E)",
                  fontFamily: "var(--font-mono)",
                  marginBottom: 16,
                }}
              >
                STEP {s.n}
              </div>
              <h3
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  letterSpacing: "-0.2px",
                  margin: "0 0 12px",
                  color: "var(--ink)",
                }}
              >
                {s.title}
              </h3>
              <p style={{ fontSize: 14.5, lineHeight: 1.55, color: "var(--body)", margin: 0 }}>
                {s.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------- Agent band ---------- */

function AgentBand() {
  return (
    <section id="agents" style={{ background: "#0e0e10", color: "#fff", padding: "120px 0" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 32px" }}>
        <Reveal>
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: "#ff6b00",
            }}
          >
            Agent-native via MCP
          </span>
        </Reveal>
        <Reveal delay={100}>
          <h2
            style={{
              fontSize: "clamp(36px, 5vw, 60px)",
              fontWeight: 600,
              letterSpacing: "-0.7px",
              lineHeight: 1.05,
              margin: "16px 0 24px",
              maxWidth: 900,
            }}
          >
            Your agents are first-class users, not bolt-ons.
          </h2>
        </Reveal>
        <Reveal delay={180}>
          <p style={{ fontSize: 19, opacity: 0.78, maxWidth: 720, lineHeight: 1.6, margin: 0 }}>
            Every minute2sec capability is exposed as an MCP tool with scoped tokens. Approve-before-write
            policies keep humans in the loop where it matters.
          </p>
        </Reveal>
        <Reveal delay={260}>
          <div
            style={{
              marginTop: 40,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
            }}
          >
            {[
              ["create_task", "Atlas drafts tasks from your PR descriptions."],
              ["log_time", "Auto-attribute work hours to tasks from commit history."],
              ["update_doc", "Keep runbooks fresh from incident postmortems."],
              ["search", "Full-text search across tasks, docs, and people."],
            ].map(([tool, copy]) => (
              <Tilt key={tool} lift={6}>
                <div
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 8,
                    padding: 16,
                    height: "100%",
                    boxSizing: "border-box",
                    boxShadow: "0 22px 44px -26px rgba(255,107,0,0.4)",
                  }}
                >
                  <code
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 12,
                      color: "#ff6b00",
                      background: "rgba(255,107,0,0.10)",
                      padding: "2px 8px",
                      borderRadius: 3,
                    }}
                  >
                    {tool}
                  </code>
                  <p
                    style={{
                      marginTop: 12,
                      fontSize: 13,
                      color: "rgba(255,255,255,0.74)",
                      lineHeight: 1.55,
                    }}
                  >
                    {copy}
                  </p>
                </div>
              </Tilt>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */

function Faq() {
  const items = [
    {
      q: "How is multi-tenancy enforced?",
      a: "Every tenant gets its own subdomain (your-team.minute2sec.com) and a tenant_id column on every row. We enforce isolation at two layers: a SQLAlchemy before_execute listener auto-injects WHERE tenant_id, and Postgres Row-Level Security blocks anything that slips past. Either layer alone would protect you; together they survive even raw-SQL escape hatches.",
    },
    {
      q: "How does the GitHub linking work?",
      a: "Every branch starts with the task key (e.g. APO-12-fix-rendering). When a user syncs, we walk their PRs across the configured repos, parse the head branch, and link by the prefix. Their PAT is Fernet-encrypted at rest and only ever surfaces github_username and last_synced_at to the client.",
    },
    {
      q: "What's MCP — and why should I care?",
      a: "MCP (Model Context Protocol) is the open standard for connecting LLMs to tools. minute2sec exposes every product capability — create task, log time, edit doc, search — as an MCP tool. Your AI agent (Claude Desktop, your own runtime) acts as a real user with scoped tokens and an audit trail, instead of screen-scraping the web UI.",
    },
    {
      q: "Can I use my own LLM?",
      a: "Yes. MCP is transport-agnostic; any MCP-compatible client works. We don't host an LLM ourselves — your team brings its own (OpenAI, Anthropic, local, etc.). All approval-before-write policies live in our backend, regardless of which model is calling.",
    },
    {
      q: "What about pricing — per-seat?",
      a: "$15 per active user per month — one plan, everything included. Inactive users don't count, billed monthly, cancel anytime. See Pricing below for the feature list.",
    },
    {
      q: "Where does my data live?",
      a: "Postgres 16 on Railway, attachments on Cloudflare R2. Cloudflare sits in front for WAF + CDN. SOC 2 and SSO/SAML land with the Business and Enterprise tiers respectively.",
    },
  ];

  const [open, setOpen] = useState<number | null>(0);

  return (
    <section
      id="faq"
      style={{
        maxWidth: 880,
        margin: "0 auto",
        padding: "100px 32px",
      }}
    >
      <Reveal>
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: "var(--mute)",
          }}
        >
          Frequently asked questions
        </span>
      </Reveal>
      <Reveal delay={80}>
        <h2
          style={{
            fontSize: "clamp(32px, 4vw, 44px)",
            fontWeight: 600,
            letterSpacing: "-0.4px",
            margin: "12px 0 36px",
          }}
        >
          The honest answers.
        </h2>
      </Reveal>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {items.map((item, idx) => {
          const isOpen = open === idx;
          return (
            <li
              key={item.q}
              style={{
                borderTop: idx === 0 ? "1px solid var(--hairline)" : undefined,
                borderBottom: "1px solid var(--hairline)",
              }}
            >
              <Reveal>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : idx)}
                  aria-expanded={isOpen}
                  style={{
                    width: "100%",
                    background: "transparent",
                    border: "none",
                    padding: "20px 0",
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    textAlign: "left",
                    cursor: "pointer",
                    color: "var(--ink)",
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      fontSize: 17,
                      fontWeight: 500,
                      letterSpacing: "-0.1px",
                    }}
                  >
                    {item.q}
                  </span>
                  <span
                    aria-hidden
                    style={{
                      fontSize: 18,
                      color: "var(--mute)",
                      transition: "transform 180ms",
                      transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                      lineHeight: 1,
                    }}
                  >
                    +
                  </span>
                </button>
                <div
                  style={{
                    maxHeight: isOpen ? 400 : 0,
                    overflow: "hidden",
                    transition: "max-height 240ms ease-out",
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      padding: "0 0 20px",
                      fontSize: 15,
                      lineHeight: 1.65,
                      color: "var(--body)",
                      maxWidth: 720,
                    }}
                  >
                    {item.a}
                  </p>
                </div>
              </Reveal>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* ---------- Pricing ---------- */

function PricingTeaser() {
  const isMobile = useIsMobile();
  // Single per-active-user plan. The features list below is what comes with it
  // — everything we ship today, no tier-gating.
  const includedFeatures = [
    "Unlimited tasks, docs, and timesheets",
    "Sprint planning + drag-and-drop board",
    "GitHub auto-linking (PRs + commits → tasks)",
    "Per-user analytics dashboard",
    "Admin team-progress aggregate",
    "MCP-native for AI agents",
    "Multi-tenant isolation (RLS + app-layer filter)",
    "Audit log on every action",
  ];

  return (
    <section id="pricing" style={{ maxWidth: 1280, margin: "0 auto", padding: "60px 32px" }}>
      <Reveal>
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: "var(--mute)",
          }}
        >
          Pricing
        </span>
      </Reveal>
      <Reveal delay={80}>
        <h2
          style={{
            fontSize: "clamp(32px, 4vw, 48px)",
            fontWeight: 600,
            letterSpacing: "-0.5px",
            margin: "12px 0 36px",
            maxWidth: 720,
          }}
        >
          One plan. Everything included.
        </h2>
      </Reveal>
      <Reveal delay={140}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1.1fr 1fr",
            gap: 24,
            alignItems: "stretch",
            maxWidth: 900,
          }}
        >
          {/* Price card */}
          <div
            style={{
              background: "var(--canvas)",
              border: "1px solid var(--ink)",
              borderRadius: 12,
              padding: 32,
              boxShadow:
                "0 30px 18px rgba(0,0,0,0.06), 0 13px 13px rgba(0,0,0,0.10), 0 3px 7px rgba(0,0,0,0.12)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: "var(--brand-teal, #0F766E)",
                fontFamily: "var(--font-mono)",
              }}
            >
              Standard
            </span>
            <div
              style={{
                marginTop: 16,
                display: "flex",
                alignItems: "baseline",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: 64,
                  fontWeight: 600,
                  letterSpacing: "-1.2px",
                  lineHeight: 1,
                }}
              >
                $15
              </span>
              <span style={{ fontSize: 16, color: "var(--mute)" }}>
                / active user / month
              </span>
            </div>
            <p style={{ marginTop: 12, fontSize: 14, color: "var(--body)", lineHeight: 1.55 }}>
              Inactive users don't count. Billed monthly. Cancel anytime.
            </p>
            <a
              href={APP_SIGNUP_URL}
              style={{
                marginTop: "auto",
                display: "inline-block",
                background: "var(--ink)",
                color: "#fff",
                padding: "12px 22px",
                borderRadius: 4,
                fontSize: 14,
                fontWeight: 500,
                textDecoration: "none",
                width: "fit-content",
              }}
            >
              Get started
            </a>
          </div>

          {/* Feature list */}
          <div
            style={{
              padding: 32,
              border: "1px solid var(--hairline)",
              borderRadius: 12,
              background: "var(--surface-1)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: "var(--mute)",
                marginBottom: 16,
              }}
            >
              What's included
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {includedFeatures.map((feature) => (
                <li
                  key={feature}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "6px 0",
                    fontSize: 14,
                    color: "var(--body)",
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      color: "var(--brand-teal, #0F766E)",
                      fontWeight: 600,
                      flexShrink: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    ✓
                  </span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- Final CTA ---------- */

function FinalCta() {
  return (
    <section style={{ padding: "80px 32px" }}>
      <Reveal>
        <div
          style={{
            maxWidth: 1080,
            margin: "0 auto",
            background: "var(--ink)",
            color: "#fff",
            borderRadius: 16,
            padding: "56px 48px",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 32,
            position: "relative",
            overflow: "hidden",
            boxShadow:
              "0 30px 60px rgba(8,8,8,0.18), 0 12px 24px rgba(8,8,8,0.10)",
          }}
        >
          {/* decorative chromatic ribbon */}
          <span
            aria-hidden
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background:
                "linear-gradient(90deg, #7a3dff 0%, #ed52cb 25%, #3b89ff 50%, #ff6b00 75%, #00d722 100%)",
            }}
          />
          <div style={{ flex: 1, minWidth: 260 }}>
            <h2
              style={{
                fontSize: "clamp(28px, 3.5vw, 40px)",
                fontWeight: 600,
                letterSpacing: "-0.4px",
                lineHeight: 1.15,
                margin: "0 0 12px",
              }}
            >
              Start your workspace in under a minute.
            </h2>
            <p
              style={{
                fontSize: 16,
                color: "rgba(255,255,255,0.74)",
                lineHeight: 1.55,
                margin: 0,
                maxWidth: 540,
              }}
            >
              Free for up to 5 active users — no credit card required. Bring your team, your docs, and
              your timesheets into a single, agent-ready workspace.
            </p>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <a
              href={APP_SIGNUP_URL}
              style={{
                background: "#fff",
                color: "var(--ink)",
                padding: "13px 22px",
                borderRadius: 4,
                fontSize: 15,
                fontWeight: 500,
                textDecoration: "none",
              }}
            >
              Start free →
            </a>
            <a
              href="#pricing"
              style={{
                background: "transparent",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.24)",
                padding: "13px 22px",
                borderRadius: 4,
                fontSize: 15,
                fontWeight: 500,
                textDecoration: "none",
              }}
            >
              See pricing
            </a>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- Contact ---------- */

function Contact() {
  const isMobile = useIsMobile();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [company, setCompany] = useState(""); // honeypot — kept off-screen
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorText, setErrorText] = useState("");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setErrorText("");
    try {
      const resp = await fetch(`${API_BASE}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
          message: message.trim(),
          company, // honeypot
        }),
      });
      if (!resp.ok) {
        let detail = "Something went wrong. Please try again.";
        try {
          const data = (await resp.json()) as { detail?: unknown };
          if (typeof data.detail === "string") detail = data.detail;
        } catch {
          /* ignore */
        }
        setErrorText(detail);
        setStatus("error");
        return;
      }
      setStatus("sent");
      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
    } catch {
      setErrorText("Network error. Please check your connection and try again.");
      setStatus("error");
    }
  };

  return (
    <section
      id="contact"
      style={{
        padding: "96px 24px",
        background: "var(--surface-1)",
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <div style={sideLabel}>Contact</div>
        <h2
          style={{
            fontSize: 32,
            fontWeight: 600,
            letterSpacing: "-0.4px",
            margin: "4px 0 8px",
            color: "var(--ink)",
          }}
        >
          Talk to us
        </h2>
        <p style={{ color: "var(--body-mid)", fontSize: 15, margin: "0 0 28px", lineHeight: 1.6 }}>
          Questions about minute2sec, a demo, or onboarding your consultancy.
          Leave your details and we will get back to you.
        </p>

        {status === "sent" ? (
          <div
            role="status"
            style={{
              padding: "16px 18px",
              background: "rgba(0,215,34,0.08)",
              border: "1px solid rgba(0,215,34,0.3)",
              borderRadius: 8,
              color: "#047a18",
              fontSize: 14.5,
            }}
          >
            Thanks — your message is on its way. We will reply to the email you
            provided.
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: "grid", gap: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
              <label style={contactField}>
                <span style={contactLabel}>Name</span>
                <input
                  type="text"
                  required
                  maxLength={120}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={contactInput}
                  autoComplete="name"
                />
              </label>
              <label style={contactField}>
                <span style={contactLabel}>Email</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={contactInput}
                  autoComplete="email"
                />
              </label>
            </div>
            <label style={contactField}>
              <span style={contactLabel}>Phone (optional)</span>
              <input
                type="tel"
                maxLength={40}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                style={contactInput}
                autoComplete="tel"
              />
            </label>
            <label style={contactField}>
              <span style={contactLabel}>Purpose</span>
              <textarea
                required
                maxLength={4000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder="What can we help you with?"
                style={{ ...contactInput, resize: "vertical", minHeight: 120 }}
              />
            </label>

            {/* Honeypot — visually hidden, off the tab order. Bots fill it. */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
              <label>
                Company
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </label>
            </div>

            {status === "error" && (
              <div
                role="alert"
                style={{
                  padding: "10px 14px",
                  background: "rgba(238,29,54,0.06)",
                  border: "1px solid rgba(238,29,54,0.3)",
                  borderRadius: 6,
                  color: "#ee1d36",
                  fontSize: 13.5,
                }}
              >
                {errorText}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <button
                type="submit"
                disabled={status === "sending"}
                style={{
                  background: "var(--ink)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 4,
                  padding: "11px 22px",
                  fontSize: 14.5,
                  fontWeight: 550,
                  cursor: status === "sending" ? "wait" : "pointer",
                  opacity: status === "sending" ? 0.65 : 1,
                  fontFamily: "var(--font-sans)",
                }}
              >
                {status === "sending" ? "Sending…" : "Send message"}
              </button>
              <span style={{ fontSize: 12.5, color: "var(--mute)" }}>
                Or email{" "}
                <a href="mailto:admin@minute2sec.com" style={{ color: "var(--ink)" }}>
                  admin@minute2sec.com
                </a>
              </span>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

const contactField: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const contactLabel: CSSProperties = {
  fontSize: 12.5,
  fontWeight: 500,
  color: "var(--body-mid)",
};

const contactInput: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 6,
  padding: "10px 12px",
  fontSize: 14.5,
  color: "var(--ink)",
  fontFamily: "var(--font-sans)",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

/* ---------- Footer ---------- */

function Footer() {
  const isMobile = useIsMobile();
  const cols: { title: string; links: { label: string; href: string }[] }[] = [
    {
      title: "Product",
      links: [
        { label: "Tasks", href: "#features" },
        { label: "Docs", href: "#features" },
        { label: "Time", href: "#features" },
        { label: "MCP for agents", href: "#agents" },
      ],
    },
    {
      title: "Resources",
      links: [
        { label: "How it works", href: "#how-it-works" },
        { label: "Pricing", href: "#pricing" },
        { label: "FAQ", href: "#faq" },
        { label: "Contact", href: "mailto:hello@minute2sec.com" },
      ],
    },
  ];

  return (
    <footer
      style={{
        borderTop: "1px solid var(--hairline)",
        background: "var(--surface-1)",
        marginTop: 40,
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "56px 32px 24px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr 1fr",
            gap: 48,
            marginBottom: 48,
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <LogoMark size={28} />
              <LogoWordmark size={16} />
            </div>
            <p
              style={{
                fontSize: 13,
                color: "var(--body-mid)",
                lineHeight: 1.55,
                maxWidth: 280,
                margin: 0,
              }}
            >
              Tasks, docs, and integrated timesheets in one workspace. Built API-first with MCP so
              your agents work alongside you.
            </p>
          </div>
          {cols.map((col) => (
            <div key={col.title}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: "var(--mute)",
                  marginBottom: 12,
                }}
              >
                {col.title}
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {col.links.map((link) => (
                  <li key={link.label} style={{ marginBottom: 8 }}>
                    <a
                      href={link.href}
                      style={{
                        fontSize: 13.5,
                        color: "var(--body)",
                        textDecoration: "none",
                      }}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div
          style={{
            borderTop: "1px solid var(--hairline)",
            paddingTop: 20,
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            color: "var(--mute)",
            fontSize: 12.5,
          }}
        >
          <span>© 2026 minute2sec. All rights reserved.</span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
            Built for teams that ship.
          </span>
        </div>
      </div>
    </footer>
  );
}

/* ---------- Bits ---------- */

const dot = (color: string): CSSProperties => ({
  width: 10,
  height: 10,
  borderRadius: 999,
  background: color,
});

const sideLabel: CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: 1.4,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 6,
};

const sideRow = (active: boolean): CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "5px 8px",
  fontSize: 12.5,
  borderRadius: 4,
  background: active ? "var(--surface-2)" : "transparent",
  color: active ? "var(--ink)" : "var(--body-mid)",
  fontWeight: active ? 550 : 400,
  marginBottom: 1,
});

const swatch = (color: string): CSSProperties => ({
  width: 8,
  height: 8,
  borderRadius: 2,
  background: color,
  flexShrink: 0,
});
